import React from "react";
import { ClassificationNode } from "@/types/classifications";
import { cn } from "@/lib/utils";
import { Folder, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ClassificationEditor } from "./ClassificationEditor";
import { logger } from "@/services/LoggingService";

interface ClassificationTreeProps {
  classifications: ClassificationNode;
  onUpdate?: (node: ClassificationNode) => void;
  onAddChild?: (parentId: string, newNode: Omit<ClassificationNode, 'id' | 'children'>) => void;
  onRemove?: (nodeId: string) => void;
}

interface TreeNodeProps {
  node: ClassificationNode;
  depth?: number;
  onUpdate?: (node: ClassificationNode) => void;
  onAddChild?: (parentId: string, newNode: Omit<ClassificationNode, 'id' | 'children'>) => void;
  onRemove?: (nodeId: string) => void;
}

function TreeNode({ node, depth = 0, onUpdate, onAddChild, onRemove }: TreeNodeProps) {
  const [isOpen, setIsOpen] = React.useState(true);
  const [isEditing, setIsEditing] = React.useState(false);
  const hasChildren = node.children && (node.children?.length || 0) > 0;
  
  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    logger.debug('classifications.tree', 'Opening editor modal', {
      nodeId: node.id,
      currentType: node.type,
      isEditing: true,
      timestamp: new Date().toISOString()
    });
    setIsEditing(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemove) {
      onRemove(node.id);
    }
  };

  const handleAddChild = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddChild) {
      logger.debug('classifications.tree', 'Adding child node', {
        parentId: node.id,
        parentType: node.type,
        timestamp: new Date().toISOString()
      });
      onAddChild(node.id, {
        name: "New Node",
        type: undefined
      });
    }
  };

  const handleNodeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleUpdate = (updatedNode: ClassificationNode) => {
    logger.debug('classifications.tree', 'Updating node', {
      nodeId: node.id,
      previousType: node.type,
      newType: updatedNode.type,
      updatedFields: {
        name: updatedNode.name,
        type: updatedNode.type
      },
      timestamp: new Date().toISOString()
    });

    if (onUpdate) {
      const mergedNode = {
        ...node,
        name: updatedNode.name,
        type: updatedNode.type,
      };
      onUpdate(mergedNode);
    }
    setIsEditing(false);
  };

  const handleSheetOpenChange = (open: boolean) => {
    logger.debug('classifications.tree', `Sheet ${open ? 'opening' : 'closing'}`, {
      nodeId: node.id,
      timestamp: new Date().toISOString()
    });
    setIsEditing(open);
  };
  
  return (
    <div>
      <div 
        className={cn(
          "group flex items-center gap-2 p-2 hover:bg-accent rounded-md cursor-pointer",
          depth > 0 && "ml-6"
        )}
        onClick={handleNodeClick}
      >
        <div className="flex items-center gap-2 flex-1">
          {hasChildren ? (
            isOpen ? <FolderOpen className="h-4 w-4" /> : <Folder className="h-4 w-4" />
          ) : (
            <Folder className="h-4 w-4" />
          )}
          <span className="text-sm font-medium">{node.name}</span>
          {node.type && (
            <Badge variant="secondary" className="text-xs">
              {node.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <Sheet open={isEditing} onOpenChange={handleSheetOpenChange}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(e);
                }}
              >
                Edit
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Edit Classification Node</SheetTitle>
                <SheetDescription>
                  Update the classification node details below
                </SheetDescription>
              </SheetHeader>
              <ClassificationEditor 
                node={node}
                onSave={handleUpdate}
                onCancel={() => setIsEditing(false)}
              />
            </SheetContent>
          </Sheet>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleDelete}
          >
            Delete
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleAddChild}
          >
            Add
          </Button>
        </div>
      </div>
      
      {isOpen && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode 
              key={child.id} 
              node={child} 
              depth={depth + 1}
              onUpdate={onUpdate}
              onAddChild={onAddChild}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ClassificationTree({ classifications, onUpdate, onAddChild, onRemove }: ClassificationTreeProps) {
  React.useEffect(() => {
    logger.debug('classifications.tree', 'Rendering classification tree', {
      rootNode: classifications.name,
      childCount: classifications.children?.length || 0,
      timestamp: new Date().toISOString()
    });
  }, [classifications]);

  if (!classifications) {
    return null;
  }

  return (
    <div className="space-y-4">
      <TreeNode 
        node={classifications} 
        onUpdate={onUpdate}
        onAddChild={onAddChild}
        onRemove={onRemove}
      />
    </div>
  );
}
